/**
 * §8.10 inline dispute expander for the Hours-tab work-approver review.
 *
 * Pattern: mirrors `DeclineConfirmation.tsx`, with one intentional
 * hardening — the note is REQUIRED, not merely encouraged. §8.10 step 1
 * mandates the `dispute_note` ("Work approver provides required
 * dispute_note (free-text reason; visible to expert and OF Fulfillment)"),
 * so Submit stays disabled until the textarea is non-empty. There is NO
 * soft-confirm-blank flow here, unlike decline.
 *
 * Render-only: production wires the dispute mutation (§8.10 steps 2–5 —
 * status → `escalated`; `disputed_at` / `disputed_by_user_id` /
 * `dispute_note` set; auto-approve clock stops; expert and OF Fulfillment
 * notified). Resolution-setting flows (OF mediation, §8.10 step 6) are out
 * of scope for the prototype — resolved states render from fixture data.
 *
 * The §8.10 auto-approve timer is NOT built: `hours_auto_approve_days` is
 * persisted (migration 0208, default 14) but nothing reads it,
 * EngagementHoursApproval.tla lists the timer as DEFERRED, and
 * BackgroundRoutines.tla registers only `engagement:period_close` of the seven
 * engagement timers (ENG-5401 adds the rest). So the helper copy claims nothing
 * about pausing it — do not put that clause back before the timer ships.
 *
 * Helper copy otherwise locked in the Phase 1 redline pass.
 */
import { useState } from 'react';

import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

interface DisputeExpanderProps {
  /**
   * Display label for the expert, interpolated into the helper copy — a first
   * name in the prototype scenes, any resolvable label live ("Robin Vale",
   * "The expert").
   */
  expertLabel: string;
  /**
   * Story-only seed for the reason textarea. Defaults to empty (the
   * just-clicked-Dispute state, Submit disabled).
   */
  initialNote?: string;
  /** Commit the dispute. Note is guaranteed non-empty (hard-required). */
  onSubmit: (note: string) => void;
  /** Collapse the expander without action. */
  onCancel: () => void;
}

export default function DisputeExpander({
  expertLabel,
  initialNote = '',
  onSubmit,
  onCancel,
}: DisputeExpanderProps): JSX.Element {
  const [note, setNote] = useState(initialNote);
  const trimmed = note.trim();

  return (
    <div className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-16">
      <div className="flex flex-col gap-2">
        <span className="text-on-surface subtitle-2">Dispute this submission</span>
        <span className="text-on-surface-muted body-2">
          This submission goes to OnFrontiers for resolution and stays unbilled until it&apos;s
          resolved.
        </span>
      </div>
      <Input
        label="Reason for dispute"
        multiline
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        helperText={`Explain what you're disputing. ${expertLabel} will see this note, and OnFrontiers will help mediate.`}
        placeholder="e.g. Hours include calls covered by another engagement; deliverable not received."
      />
      <div className="flex justify-end gap-8">
        <Button variant="tertiary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        {/* §8.10: note hard-required — no blank passthrough. */}
        <Button
          variant="secondary"
          size="small"
          disabled={trimmed.length === 0}
          onClick={() => onSubmit(trimmed)}
        >
          Submit dispute
        </Button>
      </div>
    </div>
  );
}
